import { useEffect, useRef } from "react";

/** CSS size of the scene. The canvas backing store is this times the DPR. */
const W = 280;
const H = 180;
const GRAINS = 1800;
const SAND = ["#C98B4F", "#D9A36A", "#B8713D", "#E2B784", "#A65F33"];
/** How long a landmark holds before the wind takes it, and the gust itself. */
const HOLD_MS = 3600;
const GUST_MS = 900;

type Draw = (c: CanvasRenderingContext2D) => void;

/** The Koutoubia with the medina's houses and a dome either side. */
const koutoubia: Draw = (c) => {
  c.fillRect(124, 40, 32, 140);
  c.fillRect(132, 22, 16, 20);
  c.fillRect(139, 6, 2, 18);
  [10, 15, 19].forEach((y, i) => {
    c.beginPath();
    c.arc(140, y, 3 - i * 0.6, 0, Math.PI * 2);
    c.fill();
  });
  c.fillRect(40, 120, 70, 60);
  c.fillRect(60, 100, 30, 22);
  c.fillRect(170, 110, 80, 70);
  c.beginPath();
  c.arc(205, 110, 18, Math.PI, 0);
  c.fill();
  c.fillRect(0, 172, 280, 8);
};

/** A riad doorway: a wall with a horseshoe arch cut out of it. */
const riadDoor: Draw = (c) => {
  c.fillRect(70, 30, 140, 150);
  c.globalCompositeOperation = "destination-out";
  c.beginPath();
  c.moveTo(108, 180);
  c.lineTo(108, 100);
  c.arc(140, 92, 36, 0.8 * Math.PI, 0.2 * Math.PI, false);
  c.lineTo(172, 180);
  c.fill();
  c.globalCompositeOperation = "source-over";
  c.fillRect(60, 24, 160, 8);
};

/** A palm on a dune. */
const palm: Draw = (c) => {
  c.beginPath();
  c.ellipse(140, 200, 170, 40, 0, Math.PI, 0);
  c.fill();
  c.lineWidth = 7;
  c.beginPath();
  c.moveTo(150, 165);
  c.quadraticCurveTo(160, 100, 138, 52);
  c.stroke();
  c.lineWidth = 5;
  for (let k = 0; k < 7; k++) {
    const a = -Math.PI + (k * Math.PI) / 6;
    c.beginPath();
    c.moveTo(138, 52);
    c.quadraticCurveTo(138 + Math.cos(a) * 30, 52 + Math.sin(a) * 30 - 10, 138 + Math.cos(a) * 58, 52 + Math.sin(a) * 40 + 16);
    c.stroke();
  }
};

const LANDMARKS = [koutoubia, riadDoor, palm];

/** Every filled pixel of a landmark, shuffled, as grain targets. */
function sample(draw: Draw): [number, number][] {
  const off = document.createElement("canvas");
  off.width = W;
  off.height = H;
  const o = off.getContext("2d", { willReadFrequently: true });
  if (!o) return [];
  o.fillStyle = "#000";
  o.strokeStyle = "#000";
  draw(o);
  const d = o.getImageData(0, 0, W, H).data;
  const pts: [number, number][] = [];
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) if (d[(y * W + x) * 4 + 3] > 128) pts.push([x + Math.random(), y + Math.random()]);
  }
  for (let i = pts.length - 1; i > 0; i--) {
    const j = (Math.random() * (i + 1)) | 0;
    [pts[i], pts[j]] = [pts[j], pts[i]];
  }
  return pts;
}

interface Grain {
  x: number;
  y: number;
  tx: number;
  ty: number;
  vx: number;
  vy: number;
  k: number;
  s: number;
  c: string;
  out: boolean;
}

/**
 * Sahara sand blowing in from the left and settling into a landmark (the
 * Koutoubia, a riad doorway, a palm on a dune), then a gust carrying it off
 * to the right while the next one arrives.
 *
 * Drawn on a canvas in an effect, so the server renders an empty canvas and
 * nothing random reaches hydration. Under reduced motion the Koutoubia is
 * drawn once, settled, and nothing moves.
 */
export function SandLandmarks() {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    ctx.scale(dpr, dpr);

    const shapes = LANDMARKS.map(sample);
    const grains: Grain[] = Array.from({ length: GRAINS }, (_, i) => ({
      x: -Math.random() * W,
      y: Math.random() * H,
      tx: 0,
      ty: 0,
      vx: 0,
      vy: 0,
      k: 0.04 + Math.random() * 0.05,
      s: 0.6 + Math.random() * 0.8,
      c: SAND[i % SAND.length],
      out: false,
    }));
    const aim = (shape: number) => {
      const pts = shapes[shape];
      grains.forEach((g, i) => {
        const [x, y] = pts[i % pts.length];
        g.tx = x;
        g.ty = y;
        g.out = false;
      });
    };

    const paint = () => {
      ctx.clearRect(0, 0, W, H);
      for (const g of grains) {
        ctx.fillStyle = g.c;
        ctx.fillRect(g.x, g.y, g.s, g.s);
      }
    };

    aim(0);
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      grains.forEach((g) => {
        g.x = g.tx;
        g.y = g.ty;
      });
      paint();
      return;
    }

    let shape = 0;
    let since = performance.now();
    let gustAt = 0;
    let raf = 0;
    const tick = (now: number) => {
      if (!gustAt && now - since > HOLD_MS) {
        gustAt = now;
        for (const g of grains) {
          g.out = true;
          g.vx = 2 + Math.random() * 3;
          g.vy = (Math.random() - 0.5) * 1;
        }
      }
      if (gustAt && now - gustAt > GUST_MS) {
        shape = (shape + 1) % shapes.length;
        aim(shape);
        for (const g of grains) {
          g.x = -10 - Math.random() * W * 0.7;
          g.y = g.ty + (Math.random() - 0.5) * 60;
        }
        gustAt = 0;
        since = now;
      }

      const t = now / 1000;
      for (const g of grains) {
        if (g.out) {
          g.vx += 0.2;
          g.x += g.vx;
          g.y += g.vy + Math.sin(t * 4 + g.x * 0.06) * 0.4;
        } else {
          const dx = g.tx - g.x;
          g.x += dx * g.k + Math.max(0, Math.min(3, dx * 0.02));
          g.y += (g.ty - g.y) * g.k + Math.sin(t * 3 + g.x * 0.04) * Math.min(1, Math.abs(dx) / 40) * 0.75;
        }
      }
      paint();
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return <canvas ref={ref} aria-hidden data-testid="stays-searching-sand" className="block" style={{ width: W, height: H }} />;
}
